import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList,
  CheckCircle,
  Clock,
  Star,
  Heart,
  MessageSquare,
  FileQuestion,
  ChevronRight,
  Send,
  Smile,
  Activity,
  Target,
  Users,
} from "lucide-react";
import { API } from "@/App";
import { formatDateLongTR } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

// Icon and color maps
const ICON_MAP = {
  ClipboardList,
  Heart,
  Star,
  MessageSquare,
  Activity,
  Users,
  Target,
  Smile,
  FileQuestion,
};

const COLOR_MAP = {
  blue: "bg-blue-100 text-blue-700",
  green: "bg-green-100 text-green-700",
  purple: "bg-purple-100 text-purple-700",
  orange: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
  pink: "bg-pink-100 text-pink-700",
  cyan: "bg-cyan-100 text-cyan-700",
  amber: "bg-amber-100 text-amber-700",
};

const EMOJI_OPTIONS = ["😴", "😊", "😤", "💪", "😐", "🎯", "❤️", "👍"];

export default function MySurveysModule({ businessId, user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "surveys"]);

  const [loading, setLoading] = useState(true);
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchSurveys();
    }
  }, [businessId]);

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const [pendingRes, completedRes] = await Promise.all([
        fetch(`${API}/surveys/member/pending?business_id=${businessId}`, {
          credentials: "include",
        }),
        fetch(`${API}/surveys/member/completed?business_id=${businessId}`, {
          credentials: "include",
        }),
      ]);

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingSurveys(data);
      }
      if (completedRes.ok) {
        const data = await completedRes.json();
        setCompletedSurveys(data);
      }
    } catch (error) {
      console.error("Error fetching surveys:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyForFilling = async (surveyId) => {
    try {
      const response = await fetch(`${API}/surveys/member/${surveyId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedSurvey(data);
        
        // Load existing answers if user has already partially filled the survey
        const existingAnswers = {};
        if (data.member_response?.answers) {
          data.member_response.answers.forEach(a => {
            existingAnswers[a.question_id] = a.answer;
          });
        }
        setAnswers(existingAnswers);
        setShowSurveyModal(true);
      }
    } catch (error) {
      console.error("Error fetching survey:", error);
      toast.error(t('common:surveyLoadError'));
    }
  };

  const handleSubmitSurvey = async () => {
    if (!selectedSurvey) return;

    // Validate required questions
    const requiredQuestions = selectedSurvey.questions?.filter((q) => q.is_required) || [];
    
    const unanswered = requiredQuestions.filter((q) => {
      const answer = answers[q.question_id];
      // Check if answer exists (including "no" for yes_no type, and empty string is NOT valid)
      if (answer === undefined || answer === null) return true;
      if (typeof answer === 'string' && answer.trim() === '') return true;
      return false;
    });

    if (unanswered.length > 0) {
      toast.error(`Lütfen tüm zorunlu soruları yanıtlayın (${unanswered.length} eksik)`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API}/surveys/member/${selectedSurvey.survey_id}/respond`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ answers }),
        }
      );

      if (response.ok) {
        toast.success(t("surveys:toast_anketYanitlarinizKaydedildiTesekkurler"));
        setShowSurveyModal(false);
        fetchSurveys();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || ClipboardList;
  };

  const getColorClass = (colorName) => {
    return COLOR_MAP[colorName] || "bg-blue-100 text-blue-700";
  };

  const renderQuestion = (question, index) => {
    const value = answers[question.question_id] !== undefined ? answers[question.question_id] : "";

    return (
      <div key={question.question_id} className="p-4 bg-slate-50 rounded-xl">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-navy text-white text-sm font-medium flex-shrink-0">
            {index + 1}
          </div>
          <div className="flex-1">
            <p className="font-medium text-navy">
              {question.text}
              {question.is_required && <span className="text-red-500 ml-1">*</span>}
            </p>
          </div>
        </div>

        {/* Text type */}
        {question.type === "text" && (
          <Textarea
            value={value}
            onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
            placeholder={t("surveys:ph_yanitiniziYazin")}
            className="rounded-xl"
            rows={3}
          />
        )}

        {/* Yes/No type */}
        {question.type === "yes_no" && (
          <RadioGroup
            value={value}
            onValueChange={(v) => handleAnswerChange(question.question_id, v)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id={`${question.question_id}-yes`} />
              <Label htmlFor={`${question.question_id}-yes`} className="cursor-pointer">
                Evet
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id={`${question.question_id}-no`} />
              <Label htmlFor={`${question.question_id}-no`} className="cursor-pointer">
                Hayır
              </Label>
            </div>
          </RadioGroup>
        )}

        {/* Rating type */}
        {question.type === "rating" && (
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => handleAnswerChange(question.question_id, rating.toString())}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  value === rating.toString()
                    ? "bg-amber-500 text-white scale-110"
                    : "bg-white border hover:bg-amber-50"
                }`}
              >
                <Star
                  className={`w-5 h-5 ${
                    value === rating.toString() ? "fill-current" : ""
                  }`}
                />
              </button>
            ))}
          </div>
        )}

        {/* Emoji type */}
        {question.type === "emoji" && (
          <div className="flex gap-2 flex-wrap">
            {(question.options || EMOJI_OPTIONS).map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => handleAnswerChange(question.question_id, emoji)}
                className={`text-2xl p-3 rounded-xl transition-all ${
                  value === emoji
                    ? "bg-navy scale-110"
                    : "bg-white border hover:bg-slate-100"
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Multiple choice type */}
        {question.type === "multiple_choice" && question.options && (
          <RadioGroup
            value={value}
            onValueChange={(v) => handleAnswerChange(question.question_id, v)}
            className="space-y-2"
          >
            {question.options.map((option, i) => (
              <div
                key={i}
                className={`flex items-center space-x-3 p-3 rounded-xl border transition-colors ${
                  value === option ? "bg-navy/5 border-navy" : "hover:bg-slate-100"
                }`}
              >
                <RadioGroupItem value={option} id={`${question.question_id}-${i}`} />
                <Label
                  htmlFor={`${question.question_id}-${i}`}
                  className="cursor-pointer flex-1"
                >
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy">Anketlerim</h1>
        <p className="text-grey text-sm">{t("surveys:sizeAtananAnketleriBuradanGoruntuleyebil")}</p>
      </div>

      {/* Pending Surveys */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Doldurulacak Anketler
            {pendingSurveys.length > 0 && (
              <Badge className="bg-orange-100 text-orange-700">
                {pendingSurveys.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingSurveys.length === 0 ? (
            <div className="text-center py-8 text-grey">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium text-green-700">Tüm anketleri doldurdunuz!</p>
              <p className="text-sm mt-1">{t("surveys:yeniAnketlerBuradaGorunecek")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingSurveys.map((survey) => {
                const IconComp = getIconComponent(survey.icon);
                return (
                  <div
                    key={survey.survey_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => fetchSurveyForFilling(survey.survey_id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${getColorClass(survey.color)}`}>
                        <IconComp className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy">{survey.title}</h3>
                        <p className="text-sm text-grey">
                          {survey.question_count || 0} soru
                          {survey.description && ` • ${survey.description}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-orange-100 text-orange-700">Bekliyor</Badge>
                      <ChevronRight className="w-5 h-5 text-grey" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Surveys */}
      {completedSurveys.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Tamamlanan Anketler
              <Badge className="bg-green-100 text-green-700">
                {completedSurveys.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {completedSurveys.map((response) => (
                <div
                  key={response.response_id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">
                        {response.survey_title || "Anket"}
                      </p>
                      <p className="text-xs text-green-600">
                        {formatDateLongTR(response.created_at)}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-200 text-green-800">{t('common:completed')}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Survey Fill Modal */}
      <Dialog open={showSurveyModal} onOpenChange={setShowSurveyModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedSurvey && (
                <>
                  {(() => {
                    const IconComp = getIconComponent(selectedSurvey.icon);
                    return (
                      <div className={`p-2 rounded-lg ${getColorClass(selectedSurvey.color)}`}>
                        <IconComp className="w-5 h-5" />
                      </div>
                    );
                  })()}
                  {selectedSurvey.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedSurvey && (
            <div className="space-y-4">
              {selectedSurvey.description && (
                <p className="text-grey text-sm bg-slate-50 p-3 rounded-xl">
                  {selectedSurvey.description}
                </p>
              )}

              <div className="space-y-4">
                {selectedSurvey.questions?.map((question, index) =>
                  renderQuestion(question, index)
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowSurveyModal(false)}
                  className="flex-1 rounded-xl"
                  disabled={submitting}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSubmitSurvey}
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
